/* eslint-disable no-param-reassign */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import localization from '../../shared/strings';
import prepareSearchQuery from '../../helpers/assets/prepareSearchQuery';
import { getSearchProps } from '../../helpers/history';

import { selectProductSelectionPayload } from '../selectors/productSelectionSelectors';
import { recordStateFromCatalogFilter } from './helpers/selectionContext';

export const clearAggregatedProductDetails = createAction('products/clearAggregatedProductDetails');

export const fetchAggregatedProductDetails = createAsyncThunk(
  'products/fetchAggregatedProductDetails',
  async (_arg, { getState, extra: { sdk, Logger, Toast }, signal }) => {
    const state = getState();
    const { ui: { selection } } = state.products;
    const { context, isComplete } = selection;
    const items = selectProductSelectionPayload(state);

    if (!context || (!isComplete && !items.length)) return null;

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const payload = isComplete
      ? { type: context.type, query: prepareSearchQuery() }
      : { type: context.type, items: items.map((item) => item.id) };
    const forcedRecordState = context.type === 'channel'
      ? recordStateFromCatalogFilter(getSearchProps().listingStatus)
      : null;
    if (forcedRecordState) payload.recordState = forcedRecordState;

    try {
      const { data } = await sdk.products.getAggregatedDetails(payload);
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      return data;
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) {
        throw error;
      }
      Logger.error(new Error('Error fetching aggregated product details'), { error }, [
        'FetchAggregatedProductDetailsFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      if (_arg?.silent) {
        Toast?.(localization.PRODUCT_INFO.aggregated.refreshFailed, {
          type: 'warning',
          autoClose: 5000,
        });
      }
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(clearAggregatedProductDetails, (state) => {
      state.ui.aggregatedDetails = {
        status: 'idle',
        requestId: state.ui.aggregatedDetails.requestId,
        context: null,
        recordIds: [],
        data: null,
        error: null,
        savingByKey: {},
        lastAppliedByKey: {},
      };
    })
    .addCase(fetchAggregatedProductDetails.pending, (state, { meta }) => {
      const { selection } = state.ui;
      const keepVisible = Boolean(
        meta.arg?.silent && state.ui.aggregatedDetails.status === 'succeeded',
      );

      state.ui.aggregatedDetails = {
        ...state.ui.aggregatedDetails,
        status: keepVisible ? state.ui.aggregatedDetails.status : 'loading',
        requestId: meta.requestId,
        context: selection.context,
        recordIds: selection.keys.map((key) => state.ui.selection.byKey[key]).filter(Boolean).map(
          (entry) => ({ type: entry.type, id: entry._id }),
        ),
        error: keepVisible ? state.ui.aggregatedDetails.error : null,
      };
    })
    .addCase(fetchAggregatedProductDetails.fulfilled, (state, { payload, meta }) => {
      if (meta.requestId !== state.ui.aggregatedDetails.requestId) return;

      state.ui.aggregatedDetails = {
        ...state.ui.aggregatedDetails,
        status: 'succeeded',
        data: payload,
        error: null,
      };
    })
    .addCase(fetchAggregatedProductDetails.rejected, (state, { error, meta }) => {
      if (meta.requestId !== state.ui.aggregatedDetails.requestId) return;

      if (error?.name === 'AbortError') return;

      if (meta.arg?.silent && state.ui.aggregatedDetails.data) {
        return;
      }

      state.ui.aggregatedDetails = {
        ...state.ui.aggregatedDetails,
        status: 'failed',
        error: error?.message || 'Cannot load aggregated details',
      };
    });
};
